import { Component, computed, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ActivatedRoute, Router } from '@angular/router'
import {
  ApiService,
  AppointmentDataService,
  arrayEqual,
  ChargeGrouping,
  ChargeRowFormValue,
  CharityCareStatus,
  Clinic,
  ClinicService,
  ConfirmService,
  CrudService,
  Estimate,
  EstimateCharge,
  EstimateChargeDiscountType,
  EstimateData,
  EstimateInputs,
  EstimateModel,
  EstimateOutput,
  EstimateService,
  estimateStatusText,
  Invoice,
  InvoiceModel,
  Patient,
  PatientDataService,
  ShopperEstimate,
  ShopperEstimateService,
  ShopperEstimateViewFields,
  TransactionModel,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import moment from 'moment-timezone'
import { combineLatest, forkJoin, merge, Observable, of, ReplaySubject, Subject } from 'rxjs'
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators'

import { buildPricerTraceMessage, findPricerTraceEntry, hasPricerTrace, openPricerTraceDialog } from '../../pricer-trace-message'
import { EstimateHistoryDialogComponent } from '../estimate-history'

type ShopperEstimateView = EstimateData & ShopperEstimateViewFields

export type NormalizedCharge = {
  chargeCode: string | null;
  quantity: number;
  modifiers: string[];
  visitNumbers: number[];
  cptDiscount: number,
  discountType: EstimateChargeDiscountType,
  discountedUnits: number
};

@Component({
  selector: 'mmx-estimate-summary',
  templateUrl: './estimate-summary.component.html',
  styleUrls: ['./estimate-summary.component.scss'],
  standalone: false,

})
export class EstimateSummaryComponent implements OnInit, OnDestroy {
  @Input() embedded = false
  @Input() shopper = false
  @Input() shopperInsurances?: ShopperEstimate['insurances']
  @Input() shopperEligibilityId?: string

  @Input() patient?: Patient

  @Input({ required: true }) set estimate(estimate: ShopperEstimateView) {
    this.estimate$.next(estimate)
  }

  private _estimate?: ShopperEstimateView

  get estimate() {
    return this._estimate
  }

  @Output() estimateDeleted = new EventEmitter<void>()
  @Output() viewHistory = new EventEmitter<void>()
  output?: EstimateOutput
  needsPricing$ = this.estimateService.needsPricing$
  clinic$: Observable<Clinic>
  invoices: InvoiceModel[] = []
  estimateStatusText = estimateStatusText
  initialEstimate?: EstimateData
  initialShopperFormValue?: ReturnType<ShopperEstimateService['shopperForm']['getRawValue']>
  isNewEstimate?: boolean
  estimate$ = new ReplaySubject<EstimateData>(1)
  validChargesLength = computed(() => this.estimate?.charges?.filter(c => !!c.cptCode).length ?? 0)
  calcAndSaveDisabledSubSet = false
  CHARITY_CARE_STATUS = CharityCareStatus
  ESTIMATE_STATUS = Estimate.STATUS
  INVOICE_STATUS = Invoice.STATUS
  PRICER_STATUS = EstimateOutput.PRICER_STATUS

  get canDeleteEstimate(): boolean {
    if (!this.estimate?.id || this.estimate?.status === Estimate.STATUS.DELIVERED) {
      return false
    }

    return this.hasPaidOrPartialInvoices() === false
  }

  get canModifyEstimate(): boolean {
    return this.hasPaidOrPartialInvoices() === false
  }

  get canShowModifyButton(): boolean {
    return this.estimate?.status === this.ESTIMATE_STATUS.CONFIRMED
  }

  get canShowHistoryButton(): boolean {
    return !!this.estimate?.id && !this.shopper
  }

  private destroy$ = new Subject<void>()
  private savedEstimateIdAwaitingRoute?: string
  private persistedBaselines = new Map<string, {
    estimate: EstimateData
    shopperFormValue?: ReturnType<ShopperEstimateService['shopperForm']['getRawValue']>
  }>()

  constructor(
    public estimateService: EstimateService,
    private shopperEstimateService: ShopperEstimateService,
    private crudService: CrudService,
    private router: Router,
    private confirmService: ConfirmService,
    private clinicService: ClinicService,
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private patientDataService: PatientDataService,
    private translateService: TranslateService,
    private apppointmentDataService: AppointmentDataService,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.clinic$ = this.clinicService.clinic$

    const estimateLoaded$ = combineLatest([
      this.estimateService.estimate$,
      this.estimateService.chargesLoaded$,
    ]).pipe(
      map(([est]) => est),
      filter((est): est is EstimateData => !!est),
      takeUntil(this.destroy$),
    )

    estimateLoaded$.subscribe((est) => {
      if (!est.id) {
        this.invoices = []
      } else {
        this.invoices = (est.invoices ?? []).map(inv => new InvoiceModel(inv))
      }
    })

    let initialEstimateRouteId: string | undefined
    this.estimateService.savedEstimateLoaded$.pipe(
      filter(saved => saved.id === initialEstimateRouteId && saved.status === this.ESTIMATE_STATUS.DRAFT),
      takeUntil(this.destroy$),
    ).subscribe((saved) => {
      this.initialEstimate = saved
      this.initialShopperFormValue = this.shopper ? this.shopperEstimateService.shopperForm.getRawValue() : undefined
      this.rememberPersistedBaseline(saved.id)
      if (!this.calcAndSaveDisabledSubSet) {
        this.setupDisabledCalcAndSave()
      }
      this.estimateService.checkCalcDisabledState$.next()
    })

    combineLatest([
      this.activatedRoute.params,
      this.estimate$,
    ]).pipe(
      tap(([params, estimate]) => {
        const id = params?.id || 'new'
        this.isNewEstimate = id === 'new'
        if (id !== initialEstimateRouteId) {
          this.initialEstimate = undefined
          this.initialShopperFormValue = undefined
          initialEstimateRouteId = id
        }

        const savedAwaitingRoute = id === this.savedEstimateIdAwaitingRoute ? this.persistedBaselines.get(id) : undefined

        // If estimate is NEW: no charges are saved on estimate, autopopulated charges SHOULD trigger change detection
        if (this.isNewEstimate && !this.initialEstimate) {
          this.initialEstimate = {
            ...estimate,
            charges: [],
          }
        } else if (!this.isNewEstimate && !this.initialEstimate && savedAwaitingRoute) {
          // Saving a new estimate navigates without publishing it, so estimate$ lacks the new id here.
          this.initialEstimate = savedAwaitingRoute.estimate
          this.initialShopperFormValue = savedAwaitingRoute.shopperFormValue
        // If estimate is DRAFT: charges are saved on estimate and estimate id will match url estimate id, autopopulated charges SHOULD NOT trigger change detection
        } else if (!this.isNewEstimate && estimate?.id === id && estimate?.status === this.ESTIMATE_STATUS.DRAFT && !this.initialEstimate) {
          // Until the reload lands, estimate$ for a returning draft can still hold its unsaved edits.
          const persisted = this.persistedBaselines.get(id)
          this.initialEstimate = persisted?.estimate ?? estimate
          this.initialShopperFormValue = persisted?.shopperFormValue
        }
        if (this.initialEstimate) {
          if (this.shopper && !this.initialShopperFormValue) {
            this.initialShopperFormValue = this.shopperEstimateService.shopperForm.getRawValue()
          }
          if (!this.isNewEstimate) {
            this.rememberPersistedBaseline(id)
          }
          if (!this.calcAndSaveDisabledSubSet) {
            this.setupDisabledCalcAndSave()
          }
        }

        this._estimate = estimate
        this.output = estimate?.output
        this.estimateService.checkCalcDisabledState$.next()
      }),
      debounceTime(500),
      takeUntil(this.destroy$),
    )
      .subscribe(([, estimate]) => {
        this.estimateService.checkCalcDisabledState$.next()
        this._estimate = estimate
        this.output = estimate?.output
      })
  }

  openHistoryDialog(): void {
    this.dialog.open(EstimateHistoryDialogComponent, {
      panelClass: 'dialog-form',
    })
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  onCalculate() {
    if (this.estimateService.estimateLoading$.getValue()) {
      return
    }

    if (this.shopper) {
      if (this.shopperEstimateService.shopperForm.invalid) {
        this.shopperEstimateService.shopperForm.markAllAsTouched()
        this.snackBar.open(this.translateService.instant('shopper-estimates-dashboard.messages.complete-patient-info'), null, {
          duration: 5000,
        })
        return
      }
      this.onShopperCalculate()
      return
    }

    const activeInvoices = (this.invoices || []).filter(
      (invoice) => invoice.status !== Invoice.STATUS.VOID,
    )
    const shouldVoidInvoices = !!this.estimate?.id && activeInvoices.length > 0
    const proceed$ = shouldVoidInvoices
      ? this.confirmService.confirm(
        'This estimate already has an invoice. Continuing will void the previous invoice. Do you want to continue?',
      )
      : of(true)

    const primaryInsurance = this.estimateService.primaryInsurance$.getValue()
    const secondaryInsurance = this.estimateService.secondaryInsurance$.getValue()

    if (this.estimateService.needsPricingNotice) {
      this.estimateService.needsPricingNotice.dismiss()
      this.estimateService.needsPricingNotice = undefined
    }

    this.estimateService.clearDirty()
    const estimateData = {
      ...this.estimate,
      patientId: this.estimateService.patientId$.getValue(),
      facilityId: this.estimateService.estimateForm.controls.facilityId.value,
      appointmentIds: this.estimateService.estimateForm.controls.appointmentIds.value || [],
      charges: this.estimateService.getChargesFromForm(),
      insurances: {
        P: primaryInsurance
          ? { insuranceId: primaryInsurance.id, eligibilityId: primaryInsurance.eligibilityId }
          : undefined,
        S: secondaryInsurance
          ? { insuranceId: secondaryInsurance.id, eligibilityId: secondaryInsurance.eligibilityId }
          : undefined,
      },
      // this.estimate is a stale snapshot from load time - originalBenefits$/
      // originalSecondaryBenefits$ are what EstimateComponent's benefits$/secondaryBenefits$
      // subscriptions actually keep current on every passive or explicit refresh (PIA-3167 T4).
      originalBenefits: this.estimateService.originalBenefits$.getValue(),
      originalSecondaryBenefits: this.estimateService.originalSecondaryBenefits$.getValue(),
    }
    const estimateToSave = new EstimateModel(estimateData)

    proceed$
      .pipe(
        takeUntil(this.destroy$),
        filter(Boolean),
        tap(() => this.estimateService.estimateLoading$.next(true)),
        switchMap(() => {
          if (!shouldVoidInvoices) {
            return of(null)
          }

          return forkJoin(
            activeInvoices.map((invoice) =>
              this.apiService.delete<void>(`/invoices/${invoice.id}`),
            ),
          ).pipe(
            tap(() => {
              const voidedIds = new Set(activeInvoices.map((invoice) => invoice.id))
              this.invoices = (this.invoices || []).map((invoice) => {
                if (voidedIds.has(invoice.id)) {
                  invoice.status = Invoice.STATUS.VOID
                }
                return invoice
              })
              const currentEstimate = this.estimate
              if (currentEstimate) {
                this.estimateService.setEstimate({
                  ...currentEstimate,
                  invoices: this.invoices.map((inv) => inv.toJSON?.() ?? inv),
                })
              }
            }),
          )
        }),
        switchMap(() => this.crudService.save(estimateToSave)),
      )
      .subscribe(updated => {
        this.estimateService.needsPricing$.next(false)
        this.patientDataService.refreshDocuments$.next()
        if (!this.estimate.id && !this.embedded) {
          const patientId = this.estimateService.patientId$.getValue()
          if (patientId) {
            this.savedEstimateIdAwaitingRoute = updated.id
            this.router.navigate(['/patient', patientId, 'estimates', updated.id], {
              // Keep query params when saving to prevent unnecessary estimate$ trigger in estimate.component
              queryParamsHandling: 'preserve',
            })
          }
        } else {
          this.estimateService.setEstimate(updated)
          this.apppointmentDataService.appointmentUpdated$.next()
        }
        this.initialEstimate = this.estimateService.estimateModelToEstimateData(updated)
        this.rememberPersistedBaseline(updated.id)
        this.estimateService.estimateLoading$.next(false)
        this.estimateService.checkCalcDisabledState$.next()

      }, () => {
        this.estimateService.estimateLoading$.next(false)
        this.snackBar.open('An error ocurred while generating an estimate. Please try again.', null, {
          duration: 5000,
        })
      })
  }

  private onShopperCalculate() {
    if (this.estimateService.needsPricingNotice) {
      this.estimateService.needsPricingNotice.dismiss()
      this.estimateService.needsPricingNotice = undefined
    }
    this.estimateService.clearDirty()

    const shopperFormValue = this.shopperEstimateService.shopperForm.getRawValue()
    const shopperInsurances = this.shopperInsurances ?? {}
    const inputs = {
      ...(this.estimate?.inputs ?? {}),
    } as EstimateInputs
    const payload = {
      patientFirstName: shopperFormValue.patientFirstName,
      patientLastName: shopperFormValue.patientLastName,
      // The date picker may store a moment.Moment; serialize to the YYYY-MM-DD
      // string the API and search filter expect (model contract is date-only).
      patientDOB: shopperFormValue.patientDOB
        ? moment(shopperFormValue.patientDOB).format('YYYY-MM-DD')
        : shopperFormValue.patientDOB,
      patientEmail: shopperFormValue.patientEmail,
      patientPhone: shopperFormValue.patientPhone,
      facilityId: this.estimateService.estimateForm.controls.facilityId.value,
      charges: this.estimateService.getChargesFromForm(),
      insurances: {
        P: shopperInsurances.P ?? undefined,
      },
      eligibilityId: this.shopperEligibilityId,
      inputs,
    }

    const isNew = !this.estimate?.id
    const save$ = isNew
      ? this.shopperEstimateService.calculateNew(payload)
      : this.shopperEstimateService.calculate(this.estimate.id, payload)

    this.estimateService.estimateLoading$.next(true)
    save$.pipe(
      takeUntil(this.destroy$),
      finalize(() => this.estimateService.estimateLoading$.next(false)),
    ).subscribe({
      next: (updated) => {
        this.estimateService.needsPricing$.next(false)
        const shopperEstimateState = this.toShopperEstimateState(updated, this.estimate)
        this.estimateService.setEstimate(shopperEstimateState)
        this.initialEstimate = shopperEstimateState
        this.initialShopperFormValue = this.shopperEstimateService.shopperForm.getRawValue()
        this.rememberPersistedBaseline(updated.id)
        this.estimateService.checkCalcDisabledState$.next()
        if (isNew) {
          this.savedEstimateIdAwaitingRoute = updated.id
          this.router.navigate(['/shopper-estimates', updated.id])
        }
      },
      error: () => {
        this.snackBar.open(this.translateService.instant('shopper-estimates-dashboard.messages.estimate-error'), null, {
          duration: 5000,
        })
      },
    })
  }

  onDelete() {
    if (!this.canDeleteEstimate) {
      return
    }

    if (this.shopper) {
      this.shopperEstimateService.delete(this.estimate.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.estimateDeleted.emit()
          },
          error: (err) => {
            this.snackBar.open(err?.error?.message ?? 'An Error Occurred', undefined, { duration: 5000 })
          },
        })
      return
    }

    const estimateToDelete = new EstimateModel(this.estimate)
    this.crudService.delete(estimateToDelete)
      .pipe(
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.estimateDeleted.emit()
        },
        error: (err) => {
          this.snackBar.open(err?.error?.message ?? 'An Error Occurred', undefined, { duration: 5000 })
        },
      })
  }

  pbTrace(chargeGrouping: ChargeGrouping) {
    const response = findPricerTraceEntry(this.estimate.pbResponses, chargeGrouping)
    const request = findPricerTraceEntry(this.estimate.pbRequests, chargeGrouping)

    const t = (key: string, params?: object) =>
      this.translateService.instant(`patient-details.estimate-card.advanced.${key}`, params)

    const message = buildPricerTraceMessage('pb', response, request, t)
    if (!message) {
      return
    }

    openPricerTraceDialog(this.confirmService, message, this.destroy$)
  }

  hbTrace(chargeGrouping: ChargeGrouping) {
    const response = findPricerTraceEntry(this.estimate.hbResponses, chargeGrouping)
    const request = findPricerTraceEntry(this.estimate.hbRequests, chargeGrouping)

    const t = (key: string, params?: object) =>
      this.translateService.instant(`patient-details.estimate-card.advanced.${key}`, params)

    const message = buildPricerTraceMessage('hb', response, request, t)
    if (!message) {
      return
    }

    openPricerTraceDialog(this.confirmService, message, this.destroy$)
  }

  isInsuranceResponsibilityCharge(charge: EstimateCharge): boolean {
    if (!charge?.modifiers) {
      return false
    }

    const modifiers = charge.modifiers
      .flatMap(modifier =>
        modifier.split(/[\s,/-]+/)
          .map(m => m.trim().toUpperCase())
          .filter(Boolean),
      )

    return modifiers.includes('33')
  }

  onModify() {
    const current = this.estimate
    if (!current) {
      return
    }

    if (!this.canModifyEstimate) {
      return
    }

    if (this.shopper) {
      this.estimateService.estimateLoading$.next(true)
      this.shopperEstimateService.markDraft(current.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (updated) => {
            this.estimateService.setEstimate({
              ...this.toShopperEstimateState(updated, current),
              status: Estimate.STATUS.DRAFT,
            })
            this.estimateService.markDirty()
            this.estimateService.estimateLoading$.next(false)
          },
          error: () => {
            this.estimateService.estimateLoading$.next(false)
            this.snackBar.open(this.translateService.instant('shopper-estimates-dashboard.messages.estimate-update-error'), null, {
              duration: 5000,
            })
          },
        })
      return
    }

    this.estimateService.setEstimate({ ...current, status: Estimate.STATUS.DRAFT })
    this.estimateService.markDirty()
    // Now editable - pull in any benefits that changed while this estimate was locked.
    this.estimateService.notifyBenefitsChanged()
  }

  onNewCharge(charge: { invoice: InvoiceModel, transaction: TransactionModel }) {
    this.snackBar.open('Charge collected successfully', undefined, { duration: 3000 })
    this.router.navigate(['/patient', this.patient.id, 'transaction', charge.transaction.id])
  }

  createInvoices() {
    if (!this.estimate?.id) {
      return
    }

    if (this.shopper) {
      this.estimateService.estimateLoading$.next(true)
      this.shopperEstimateService.finalize(this.estimate.id)
        .pipe(
          takeUntil(this.destroy$),
        )
        .subscribe({
          next: (updated) => {
            const shopperEstimateState = this.toShopperEstimateState(
              updated,
              this.estimate,
            )
            this.estimateService.setEstimate(shopperEstimateState)
            this.estimateService.estimateLoading$.next(false)
            this.snackBar.open(this.translateService.instant('shopper-estimates-dashboard.messages.estimate-finalized'), undefined, { duration: 3000 })
          },
          error: () => {
            this.estimateService.estimateLoading$.next(false)
            this.snackBar.open(this.translateService.instant('shopper-estimates-dashboard.messages.estimate-finalize-error'), null, { duration: 5000 })
          },
        })
      return
    }

    const appointmentIds = this.estimateService.estimateForm.controls.appointmentIds.value || []
    const mismatch = this.checkMismatchedVisitNumberAppointments()

    const proceed$ = mismatch
      ? this.confirmService.confirm(
        this.translateService.instant('patient-details.estimate-card.visit-count-warning'),
      )
      : of(true)

    proceed$
      .pipe(
        takeUntil(this.destroy$),
        filter(Boolean),
        tap(() => this.estimateService.estimateLoading$.next(true)),
        switchMap(() => this.apiService.createInvoicesFromEstimate(this.estimate.id)),
        switchMap((newInvoices) => {
          if (appointmentIds.length > 0) {
            return this.apiService.getInvoices({ appointmentIds }).pipe(
              map(response => response.data),
              catchError(() => of(newInvoices)),
            )
          }
          return of(newInvoices)
        }),
      ).subscribe((invoices) => {
        this.invoices = invoices
        const updated = {
          ...this.estimate,
          status: Estimate.STATUS.CONFIRMED,
          invoices: invoices.map((inv) => inv.toJSON?.() ?? inv),
        }
        this.estimateService.setEstimate(updated)
        this.estimateService.estimateLoading$.next(false)
        this.snackBar.open('Invoices created', undefined, { duration: 3000 })
      })
  }

  onPrintShopperPDF() {
    if (!this.estimate?.id) {
      return
    }

    this.generateShopperPDF().subscribe({
      next: (link) => {
        if (typeof window !== 'undefined') {
          const url = typeof link === 'string' ? link : link.url
          window.open(url, '_blank', 'noopener,noreferrer')
        }
      },
      error: () => {
        this.snackBar.open(this.translateService.instant('shopper-estimates-dashboard.messages.pdf-error'), null, { duration: 5000 })
      },
    })
  }

  private generateShopperPDF() {
    this.estimateService.estimateLoading$.next(true)
    return this.shopperEstimateService.downloadPDF(this.estimate.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.estimateService.estimateLoading$.next(false)),
      )
  }

  hasPBTrace(grouping: ChargeGrouping): boolean {
    return hasPricerTrace(this.estimate.pbResponses, this.estimate.pbRequests, grouping)
  }

  hasHBTrace(grouping: ChargeGrouping): boolean {
    return hasPricerTrace(this.estimate.hbResponses, this.estimate.hbRequests, grouping)
  }

  private hasPaidOrPartialInvoices(): boolean {
    return (this.invoices || []).some((invoice) => {
      const statuses = [
        Invoice.STATUS.PAID,
        Invoice.STATUS.PARTIAL,
        Invoice.STATUS.PAYING,
      ]

      if (statuses.includes(invoice.status)) {
        return true
      }

      if (invoice.amountPaid) {
        return true
      }

      if (invoice.estimate) {
        // This is an immediate fix for when there are cpt discounts
        // However, we need to determine if the patientResposibility should be reflected on the invoice
        // Currently, when creating an invoice from an estimate, it currently sets the amount due to the patient responsibility
        // From the systems point of view, it appears as if a payment has been made (the amount of the discount)
        // This causes confusion in places like this where we check if there are paid/partial invoices
        if (invoice.amountRemaining < invoice.estimate.patientsResponsibility) {
          return true
        }
      } else {
        if (invoice.amountRemaining < invoice.total) {
          return true
        }
      }

      return false
    })
  }

  setupDisabledCalcAndSave() {
    this.calcAndSaveDisabledSubSet = true
    this.estimateService.chargesArray$.pipe(
      takeUntil(this.destroy$),
      filter(Boolean),
      switchMap(chargeArrayForm => {
        const formControl = chargeArrayForm.parent ?? chargeArrayForm

        const sources = [
          formControl.valueChanges,
          this.estimateService.checkCalcDisabledState$.pipe(
            map(() => formControl.value),
          ),
          this.estimateService.estimateLoading$.pipe(
            distinctUntilChanged(),
            map(() => formControl.value),
          ),
        ]

        if (this.shopper) {
          sources.push(
            this.shopperEstimateService.shopperForm.valueChanges.pipe(
              map(() => formControl.value),
            ),
          )
        }

        return merge(
          merge(...sources).pipe(map(form => ({ form, insuranceChange: false }))),
          merge(this.estimateService.primaryInsurance$, this.estimateService.secondaryInsurance$).pipe(
            map(() => ({ form: formControl.value, insuranceChange: true })),
          ),
        )
      }),
    ).subscribe(({ form, insuranceChange }) => {
      if (!this.initialEstimate) {
        this.estimateService.calcAndSaveDisabled.next(true)
        this.estimateService.needsPricing$.next(true)
        return
      }

      const charges: ChargeRowFormValue[] = form?.charges ?? []
      const unchangedCharges = this.chargesUnchanged(
        charges,
        this.initialEstimate?.charges ?? [],
      )
      const unchangedPatientInfo = this.shopper ? this.shopperPatientInfoUnchanged() : true
      const primaryInsurance = this.estimateService.primaryInsurance$.value
      const secondaryInsurance = this.estimateService.secondaryInsurance$.value
      const hasSecondary = this.initialEstimate?.inputs?.hasSecondary === true
        || this.estimate?.inputs?.hasSecondary === true
      const unchangedInsurances = this.shopper || (
        (primaryInsurance === undefined || (primaryInsurance?.id ?? null) === (this.initialEstimate?.insurances?.P?.insuranceId ?? null))
        && (!hasSecondary || secondaryInsurance === undefined || (secondaryInsurance?.id ?? null) === (this.initialEstimate?.insurances?.S?.insuranceId ?? null))
      )
      if (insuranceChange && unchangedInsurances) {
        return
      }
      const noCharges = charges.length === 0
      const missingChargeCodes = charges.some(c => !c.chargeCode)
      const missingVisitNumbers = charges.some(c => c.visitNumbers?.length === 0)
      // discountedUnits is optional: empty/null means "apply to all units" (the backend's
      // getDiscountedUnits falls back to quantity). Only treat it as invalid when explicitly
      // set to a non-positive value — note `null <= 0` is `true` in JS, which previously
      // flagged a valid all-units discount as missing and disabled Calculate & Save.
      const missingDiscount = charges.some(c => 'discountType' in c
        && (c.cptDiscount <= 0 || (c.discountedUnits != null && c.discountedUnits <= 0)))
      const visitNumbersValid = this.estimateService.visitNumbersValid
      const mismatch = this.checkMismatchedVisitNumberAppointments()
      const invalidForm = this.estimateService.estimateForm.invalid

      const unchanged = unchangedCharges && unchangedPatientInfo && unchangedInsurances
      const isInvalid = noCharges || missingChargeCodes || missingVisitNumbers || mismatch || !visitNumbersValid || missingDiscount || invalidForm
      const calcAndSaveDisabled = isInvalid || unchanged || this.estimateService.estimateLoading$.value
      this.estimateService.calcAndSaveDisabled.next(calcAndSaveDisabled)
      this.estimateService.needsPricing$.next(isInvalid || !unchanged)
    })
  }

  private rememberPersistedBaseline(id: string | undefined) {
    if (id && this.initialEstimate) {
      this.initialEstimate = {
        ...this.initialEstimate,
        inputs: this.initialEstimate.inputs ? { ...this.initialEstimate.inputs } : this.initialEstimate.inputs,
        charges: this.initialEstimate.charges?.map(charge => ({
          ...charge,
          modifiers: charge.modifiers?.slice(),
          visitNumbers: charge.visitNumbers?.slice(),
        })),
      }
      this.persistedBaselines.set(id, {
        estimate: this.initialEstimate,
        shopperFormValue: this.initialShopperFormValue,
      })
    }
  }

  private shopperPatientInfoUnchanged(): boolean {
    if (!this.initialShopperFormValue) {
      return true
    }

    const current = this.shopperEstimateService.shopperForm.getRawValue()
    const toDateOnly = (dob: string | moment.Moment | null) => dob ? moment(dob).format('YYYY-MM-DD') : dob
    return current.patientFirstName === this.initialShopperFormValue.patientFirstName
      && current.patientLastName === this.initialShopperFormValue.patientLastName
      && toDateOnly(current.patientDOB) === toDateOnly(this.initialShopperFormValue.patientDOB)
      && current.patientEmail === this.initialShopperFormValue.patientEmail
      && current.patientPhone === this.initialShopperFormValue.patientPhone
  }

  chargesUnchanged(
    current: ChargeRowFormValue[],
    initial: EstimateCharge[],
  ): boolean {
    if (current.length !== initial.length) {
      return false
    }

    const sortKey = (c: NormalizedCharge) => `${c.chargeCode ?? ''}|${(c.visitNumbers ?? []).join(',')}`
    const normalizedInitial = initial.map(c => this.normalizeCharge(c)).sort((a, b) => sortKey(a).localeCompare(sortKey(b)))
    const normalizedCurrent = current.map(c => this.normalizeCharge(c)).sort((a, b) => sortKey(a).localeCompare(sortKey(b)))

    return normalizedCurrent.every((curr, i) => this.chargeEqual(curr, normalizedInitial[i]))
  }

  normalizeCharge(
    c: ChargeRowFormValue | EstimateCharge,
  ): NormalizedCharge {

    const normalizeModifiers = (mods: string[] | string | undefined): string[] => {
      if (!mods) {
        return []
      }

      if (Array.isArray(mods)) {
        return mods
          .flatMap(m =>
            m.split(/[\s,/-]+/)
              .map(x => x.trim().toUpperCase())
              .filter(Boolean),
          )
          .sort()
      }

      return mods
        .split(/[\s,/-]+/)
        .map(x => x.trim().toUpperCase())
        .filter(Boolean)
        .sort()
    }

    let cptDiscount: number
    let discountType: EstimateChargeDiscountType
    let discountedUnits: number
    const isEstimateCharge = this.isEstimateCharge(c)
    if (isEstimateCharge) {
      cptDiscount = c.cptDiscountAmount ?? null
      discountType = c.cptDiscountType ?? null
      discountedUnits = c.cptDiscountedUnits ?? null
    } else {
      cptDiscount = c.cptDiscount ?? null
      discountType = c.discountType ?? null
      discountedUnits = c.discountedUnits ?? null
    }

    return {
      chargeCode: c.chargeCode || null,
      quantity: c.quantity ?? 0,
      modifiers: normalizeModifiers(c.modifiers),
      visitNumbers: [...(c.visitNumbers ?? [])].sort((a, b) => a - b),
      cptDiscount,
      discountType,
      discountedUnits,

    }
  }

  isEstimateCharge(
    c: ChargeRowFormValue | EstimateCharge,
  ): c is EstimateCharge {
    return 'cptDiscountType' in c ||
      'cptDiscountAmount' in c ||
      'cptDiscountedUnits' in c
  }

  chargeEqual(a: NormalizedCharge, b: NormalizedCharge): boolean {
    return (
      a.chargeCode === b.chargeCode &&
    a.quantity === b.quantity &&
    a.cptDiscount === b.cptDiscount &&
    a.discountType === b.discountType &&
    a.discountedUnits === b.discountedUnits &&
    arrayEqual(a.modifiers, b.modifiers) &&
    arrayEqual(a.visitNumbers, b.visitNumbers)
    )
  }

  estimateFinalized(): boolean {
    if (this.shopper) {
      return this.estimate?.status === Estimate.STATUS.CONFIRMED
    }
    return this.estimate?.status === Estimate.STATUS.CONFIRMED ||
    this.estimate?.status === Estimate.STATUS.DELIVERED ||
    this.estimate?.status === Estimate.STATUS.UNDELIVERED
  }

  private toShopperEstimateState(
    updated: ShopperEstimate,
    source: ShopperEstimateView,
  ): ShopperEstimateView {
    const shopperInsurances = updated.insurances ?? this.shopperInsurances ?? source?.shopperInsurances
    const status = updated.status === ShopperEstimate.STATUS.FINALIZED
      ? Estimate.STATUS.CONFIRMED
      : Estimate.STATUS.DRAFT

    return {
      id: updated.id,
      facilityId: updated.facilityId ?? source.facilityId ?? '',
      charges: updated.charges,
      inputs: updated.inputs,
      output: updated.output,
      patientId: source.patientId,
      appointmentIds: source.appointmentIds,
      status,
      shopperInsurances,
      eligibilityId: updated.eligibilityId ?? this.shopperEligibilityId ?? source.eligibilityId,
      patientEmail: updated.patientEmail,
      patientPhone: updated.patientPhone,
    }
  }

  /**
   * Checks if the number of unique visit numbers applied to charges matches
   * the number of selected appointments. This ensures all appointments have
   * at least one charge assigned to them.
   *
   * Note: A single charge CAN have multiple visit numbers (e.g., [1, 2, 3]),
   * so this check looks at the unique set of visit numbers across all charges.
   *
   * @returns True if there's a mismatch, false otherwise
   */
  private checkMismatchedVisitNumberAppointments() {
    const selectedAppointmentIds = this.estimateService.estimateForm.controls.appointmentIds.value.length
    if (selectedAppointmentIds === 0) {
      return false
    }
    const appliedVisitCount = this.estimateService.selectedVisitNumbers$.getValue()?.length || 0
    return appliedVisitCount !== selectedAppointmentIds
  }
}
